import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  Alert,
  Empty,
  Kpi,
  KpiGrid,
  Muted,
  PageHeader,
  Section,
  StatusBadge,
  Tabs,
} from '@/components/ui'
import {
  comentarRedePost,
  criarRedePost,
  fetchRedeCanais,
  fetchRedeEventos,
  fetchRedeFeed,
  fetchRedeRanking,
  reagirRedePost,
} from './api'

type TabId = 'feed' | 'ranking' | 'eventos' | 'canais'

function formatQuando(iso?: string) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

export function RedePage() {
  const { canEdit } = useAuth()
  const qc = useQueryClient()
  const [tab, setTab] = useState<TabId>('feed')
  const [tipo, setTipo] = useState('')
  const [titulo, setTitulo] = useState('')
  const [conteudo, setConteudo] = useState('')
  const [comentarioDraft, setComentarioDraft] = useState<Record<string, string>>({})
  const [msg, setMsg] = useState('')

  const feed = useQuery({
    queryKey: ['rede', 'feed', tipo],
    queryFn: () => fetchRedeFeed({ tipo: tipo || undefined, page: 1 }),
  })
  const ranking = useQuery({
    queryKey: ['rede', 'ranking'],
    queryFn: () => fetchRedeRanking(10),
    enabled: tab === 'ranking',
  })
  const eventos = useQuery({
    queryKey: ['rede', 'eventos'],
    queryFn: () => fetchRedeEventos(),
    enabled: tab === 'eventos',
  })
  const canais = useQuery({
    queryKey: ['rede', 'canais'],
    queryFn: () => fetchRedeCanais(),
    enabled: tab === 'canais',
  })

  const criar = useMutation({
    mutationFn: () =>
      criarRedePost({
        conteudo: conteudo.trim(),
        titulo: titulo.trim() || undefined,
        tipo: 'geral',
      }),
    onSuccess: async () => {
      setTitulo('')
      setConteudo('')
      setMsg('Publicação enviada.')
      await qc.invalidateQueries({ queryKey: ['rede', 'feed'] })
    },
    onError: (err) => {
      setMsg(err instanceof ApiError ? err.message : 'Falha ao publicar.')
    },
  })

  const reagir = useMutation({
    mutationFn: (postId: string) => reagirRedePost(postId, 'curtir'),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['rede', 'feed'] })
    },
  })

  const comentar = useMutation({
    mutationFn: ({ postId, texto }: { postId: string; texto: string }) =>
      comentarRedePost(postId, texto),
    onSuccess: async (_, vars) => {
      setComentarioDraft((d) => ({ ...d, [vars.postId]: '' }))
      setMsg('Comentário publicado.')
      await qc.invalidateQueries({ queryKey: ['rede', 'feed'] })
    },
    onError: (err) => {
      setMsg(err instanceof ApiError ? err.message : 'Falha ao comentar.')
    },
  })

  const posts = feed.data?.posts || []

  return (
    <section className="hi-page">
      <PageHeader
        title="Rede corporativa"
        subtitle="Feed, ranking, eventos e canais via API /api/v1/rede/."
        actions={
          <a className="btn btn-secondary" href={legacyAppUrl('#/rede-feed')}>
            Legado
          </a>
        }
      />

      {msg ? <Alert tone="info">{msg}</Alert> : null}

      <KpiGrid>
        <Kpi label="Posts no feed" value={String(posts.length)} />
        <Kpi
          label="Fixados"
          value={String(posts.filter((p) => p.fixado).length)}
        />
        <Kpi
          label="Reações"
          value={String(posts.reduce((s, p) => s + (p.total_reacoes || 0), 0))}
        />
      </KpiGrid>

      <Tabs
        label="Seções da Rede"
        value={tab}
        onChange={setTab}
        items={[
          { id: 'feed', label: 'Feed' },
          { id: 'ranking', label: 'Ranking' },
          { id: 'eventos', label: 'Eventos' },
          { id: 'canais', label: 'Canais' },
        ]}
      />

      {tab === 'feed' ? (
        <>
          {canEdit('rede-feed') ? (
            <Section title="Nova publicação">
              <form
                className="hi-form-stack"
                onSubmit={(e) => {
                  e.preventDefault()
                  if (!conteudo.trim()) {
                    setMsg('Escreva o conteúdo da publicação.')
                    return
                  }
                  criar.mutate()
                }}
              >
                <input
                  type="text"
                  placeholder="Título (opcional)"
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  aria-label="Título da publicação"
                />
                <textarea
                  rows={3}
                  placeholder="O que você quer compartilhar?"
                  value={conteudo}
                  onChange={(e) => setConteudo(e.target.value)}
                  aria-label="Conteúdo da publicação"
                  required
                />
                <button className="btn btn-primary" type="submit" disabled={criar.isPending}>
                  {criar.isPending ? 'Publicando…' : 'Publicar'}
                </button>
              </form>
            </Section>
          ) : null}

          <form
            className="hi-filters"
            onSubmit={(e) => {
              e.preventDefault()
            }}
          >
            <select value={tipo} onChange={(e) => setTipo(e.target.value)} aria-label="Filtrar por tipo">
              <option value="">Todos os tipos</option>
              <option value="comunicado">Comunicado</option>
              <option value="conquista">Conquista</option>
              <option value="cultura">Cultura</option>
              <option value="evento">Evento</option>
              <option value="geral">Geral</option>
            </select>
          </form>

          <Section title="Feed" flush>
            {feed.isLoading ? <Muted>Carregando feed…</Muted> : null}
            {feed.isError ? (
              <Alert tone="danger">
                {feed.error instanceof ApiError ? feed.error.message : 'Falha ao carregar feed.'}
              </Alert>
            ) : null}
            {!feed.isLoading && posts.length === 0 ? (
              <Empty>Nenhuma publicação neste filtro.</Empty>
            ) : null}
            <ul className="hi-list">
              {posts.map((p) => (
                <li key={p.id} className="card hi-row" style={{ alignItems: 'flex-start' }}>
                  <div className="hi-row-main" style={{ width: '100%' }}>
                    <div className="hi-actions" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
                      <strong>{p.titulo || p.tipo || 'Publicação'}</strong>
                      <StatusBadge tone={p.fixado ? 'pendente' : 'ativo'}>
                        {p.fixado ? 'Fixado' : p.tipo || 'geral'}
                      </StatusBadge>
                    </div>
                    <Muted>
                      {p.autor_nome || 'Autor'} · {formatQuando(p.created_at)}
                    </Muted>
                    <p style={{ margin: '8px 0', whiteSpace: 'pre-wrap' }}>{p.conteudo}</p>
                    <Muted>
                      👍 {p.total_reacoes ?? 0} · 💬 {p.total_comentarios ?? 0} · 👁{' '}
                      {p.total_visualizacoes ?? 0}
                    </Muted>
                    <div className="hi-actions" style={{ marginTop: 8 }}>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        disabled={reagir.isPending}
                        onClick={() => reagir.mutate(p.id)}
                      >
                        Curtir
                      </button>
                    </div>
                    <form
                      className="hi-form-stack"
                      style={{ marginTop: 8 }}
                      onSubmit={(e) => {
                        e.preventDefault()
                        const texto = (comentarioDraft[p.id] || '').trim()
                        if (!texto) return
                        comentar.mutate({ postId: p.id, texto })
                      }}
                    >
                      <input
                        type="text"
                        placeholder="Escrever comentário…"
                        value={comentarioDraft[p.id] || ''}
                        onChange={(e) =>
                          setComentarioDraft((d) => ({ ...d, [p.id]: e.target.value }))
                        }
                        aria-label={`Comentar no post ${p.titulo || p.id}`}
                      />
                      <button className="btn btn-secondary btn-sm" type="submit" disabled={comentar.isPending}>
                        Comentar
                      </button>
                    </form>
                  </div>
                </li>
              ))}
            </ul>
          </Section>
        </>
      ) : null}

      {tab === 'ranking' ? (
        <Section title="Ranking de gamificação" flush>
          {ranking.isLoading ? <Muted>Carregando…</Muted> : null}
          {ranking.isError ? (
            <Alert tone="danger">
              {ranking.error instanceof ApiError ? ranking.error.message : 'Falha ao carregar ranking.'}
            </Alert>
          ) : null}
          {!ranking.isLoading && (ranking.data || []).length === 0 ? (
            <Empty>Nenhum programa de gamificação ativo.</Empty>
          ) : null}
          <ul className="hi-list">
            {(ranking.data || []).map((r, i) => (
              <li key={`${r.colaborador}-${i}`} className="card hi-row">
                <strong>#{r.posicao ?? i + 1}</strong>
                <div className="hi-row-main">
                  <strong>{r.colaborador || 'Colaborador'}</strong>
                  <Muted>
                    {r.pontos ?? 0} pts · Nível {r.nivel ?? 1}
                  </Muted>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {tab === 'eventos' ? (
        <Section title="Próximos eventos" flush>
          {eventos.isLoading ? <Muted>Carregando…</Muted> : null}
          {eventos.isError ? (
            <Alert tone="danger">
              {eventos.error instanceof ApiError ? eventos.error.message : 'Falha ao carregar eventos.'}
            </Alert>
          ) : null}
          {!eventos.isLoading && (eventos.data || []).length === 0 ? (
            <Empty>Nenhum evento futuro cadastrado.</Empty>
          ) : null}
          <ul className="hi-list">
            {(eventos.data || []).map((ev) => (
              <li key={String(ev.id)} className="card hi-row">
                <div className="hi-row-main">
                  <strong>{ev.titulo}</strong>
                  <Muted>
                    {formatQuando(ev.data_inicio)}
                    {ev.local ? ` · ${ev.local}` : ''}
                    {ev.modalidade ? ` · ${ev.modalidade}` : ''}
                    {` · ${ev.total_inscritos ?? 0} inscritos`}
                  </Muted>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {tab === 'canais' ? (
        <Section title="Canais" flush>
          {canais.isLoading ? <Muted>Carregando…</Muted> : null}
          {canais.isError ? (
            <Alert tone="danger">
              {canais.error instanceof ApiError ? canais.error.message : 'Falha ao carregar canais.'}
            </Alert>
          ) : null}
          {!canais.isLoading && (canais.data || []).length === 0 ? (
            <Empty>Nenhum canal público ativo.</Empty>
          ) : null}
          <ul className="hi-list">
            {(canais.data || []).map((c) => (
              <li key={c.id} className="card hi-row">
                <div className="hi-row-main">
                  <strong>{c.nome}</strong>
                  <Muted>
                    {c.tipo || 'canal'}
                    {c.descricao ? ` · ${c.descricao}` : ''}
                  </Muted>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
    </section>
  )
}
